import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const footerLinks = [
  {
    title: 'Navegación',
    links: [
      { label: 'Inicio', href: '#home' },
      { label: 'Nosotros', href: '#about' },
      { label: 'Servicios', href: '#services' },
      { label: 'Contacto', href: '#contact' },
    ],
  },
  {
    title: 'Servicios',
    links: [
      { label: 'Bioinformática', href: '#services' },
      { label: 'Ciencia de Datos', href: '#services' },
      { label: 'Biología de Sistemas', href: '#services' },
      { label: 'Infraestructura', href: '#services' },
    ],
  },
];

const socialLinks = [
  { icon: 'ri-linkedin-fill', label: 'LinkedIn', href: '#' },
  { icon: 'ri-twitter-x-fill', label: 'X / Twitter', href: '#' },
  { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
  { icon: 'ri-youtube-line', label: 'YouTube', href: '#' },
];

export default function Footer() {
  const { ref, inView } = useInView(0.05);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer
      ref={ref}
      className="relative bg-biodata-surface border-t border-biodata-teal/10 overflow-hidden"
    >
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto py-16 md:py-20">
        <div
          className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <a href="#home" onClick={(e) => handleNavClick(e, '#home')} className="flex items-center mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/87cfbee4-5cd7-4011-9381-9833a6e5572a.png"
                alt="ANALITICA - Biological Sciences & Applied Research"
                className="h-8 w-auto object-contain"
              />
            </a>
            <p className="text-biodata-muted text-sm leading-relaxed mb-6">
              Transformando datos biológicos, biomédicos y ambientales en conocimiento útil, reproducible e interpretable para la investigación y la innovación.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  onClick={(e) => e.preventDefault()}
                  aria-label={social.label}
                  className="w-9 h-9 rounded-lg bg-biodata-dark border border-biodata-teal/10 flex items-center justify-center text-biodata-muted hover:text-biodata-teal hover:border-biodata-teal/30 transition-all"
                >
                  <i className={social.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((group) => (
            <div key={group.title}>
              <h4 className="text-white font-medium mb-4">{group.title}</h4>
              <ul className="space-y-3">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="text-sm text-biodata-muted hover:text-biodata-teal transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-medium mb-4">Newsletter</h4>
            <p className="text-sm text-biodata-muted mb-4">
              Recibe las últimas tendencias en bioinformática y ciencia de datos.
            </p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d86001rgv8igf6sht2fg"
              method="POST"
              onSubmit={(e) => e.preventDefault()}
              className="flex gap-2"
            >
              <input
                type="email"
                name="newsletter_email"
                placeholder="tu@email.com"
                className="flex-1 px-3 py-2 rounded-lg bg-biodata-dark/60 border border-biodata-teal/10 text-white text-sm placeholder:text-biodata-muted/50 focus:outline-none focus:border-biodata-teal/40 transition-all"
              />
              <button
                type="submit"
                className="w-10 h-10 rounded-lg bg-biodata-teal flex items-center justify-center text-white hover:bg-biodata-teal/90 transition-colors flex-shrink-0"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </form>
          </div>
        </div>

        {/* Big Brand Text */}
        <div
          className={`border-t border-biodata-teal/10 pt-8 transition-all duration-700 delay-200 ${
            inView ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-[8vw] md:text-[5vw] font-display font-bold text-biodata-card/50 leading-none select-none">
              ANALITICA
            </div>
            <div className="text-sm text-biodata-muted text-center md:text-right">
              <span>© 2026 ANALITICA. Todos los derechos reservados.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}